import type { TFunction } from "i18next";
import { circulatorPowerPercentMax } from "./parameterConfiguration";

export const MAX_POWER_DEVICE_FIELD = 9900;

export type DefaultParameterRow = { field_name: string; value: unknown };

export function getDefaultParameterDisplayUnit(fieldName: string): string | undefined {
  switch (fieldName) {
    case "water_heater_min_power":
    case "water_heater_max_power":
    case "max_installation_power":
    case "dropout_power":
    case "dropout_power_l2":
    case "dropout_power_l3":
      return "W";
    case "boost_percentage":
    case "circulator_power_percent":
      return "%";
    case "circulator_deadtime_min":
    case "duration_temperature_after_circulation_min":
      return "min";
    case "circulator_temperature":
    case "valve_temperature_limit":
    case "heater_max_temp":
    case "sanitation_temperature":
    case "dropout_t_max":
    case "t_cold":
    case "t_lukewarm":
    case "t_hot":
    case "t_very_hot":
    case "t_max":
      return "°C";
    case "sanitation_max_period_days":
      return "days";
    case "dropout_v_max":
    case "dropout_v_threshold":
    case "dropout_v_max_l2":
    case "dropout_v_threshold_l2":
    case "dropout_v_max_l3":
    case "dropout_v_threshold_l3":
      return "V";
    default:
      return undefined;
  }
}

export function getDefaultParameterNumericBounds(
  fieldName: string,
  configName?: string | null
): { min?: number; max?: number; step?: number } | undefined {
  switch (fieldName) {
    case "t_cold":
      return { min: -5, step: 1 };
    case "t_lukewarm":
    case "t_hot":
    case "t_very_hot":
    case "t_max":
    case "valve_temperature_limit":
    case "sanitation_temperature":
      return { min: 5, step: 1 };
    case "circulator_deadtime_min":
      return { min: 10, max: 1440, step: 1 };
    case "circulator_power_percent":
      return { min: 0, max: circulatorPowerPercentMax(configName), step: 1 };
    case "water_heater_min_power":
    case "water_heater_max_power":
      return { min: 0, max: MAX_POWER_DEVICE_FIELD, step: 1 };
    case "max_installation_power":
      return { min: 0, max: MAX_POWER_DEVICE_FIELD, step: 1 };
    case "boost_percentage":
      return { min: 0, max: 100, step: 1 };
    case "meter_factor":
      return { min: -10000, max: 10000, step: 0.1 };
    case "heater_max_temp":
      return { min: 5, step: 0.1 };
    case "sanitation_max_period_days":
      return { min: 1, step: 1 };
    case "sanitation_hour_start_time":
      return { min: 0, max: 23, step: 1 };
    case "dropout_t_max":
    case "dropout_power":
      return { min: 0, step: 1 };
    case "dropout_v_max":
    case "dropout_v_threshold":
    case "dropout_v_max_l2":
    case "dropout_v_threshold_l2":
    case "dropout_power_l2":
    case "dropout_v_max_l3":
    case "dropout_v_threshold_l3":
    case "dropout_power_l3":
      return { min: 0, step: 0.1 };
    case "installation_phase":
      return { min: 0, max: 3, step: 1 };
    default:
      return undefined;
  }
}

function parseNumeric(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const n = Number(String(value).trim().replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

const T_CHAIN = ["t_cold", "t_lukewarm", "t_hot", "t_very_hot", "t_max"] as const;

export function getDefaultNumericParameterErrors(
  list: DefaultParameterRow[] | undefined,
  t: TFunction<"settings">,
  configName?: string | null
): Partial<Record<string, string>> {
  const errors: Partial<Record<string, string>> = {};
  if (!Array.isArray(list)) return errors;

  const dict: Record<string, number> = {};
  for (const row of list) {
    const name = row.field_name;
    if (!name || row.value === null || row.value === undefined || row.value === "") continue;
    const parsed = parseNumeric(row.value);
    if (parsed !== null) dict[name] = parsed;
  }

  for (const row of list) {
    const name = row.field_name;
    if (!name || row.value === null || row.value === undefined || row.value === "") continue;

    const bounds = getDefaultParameterNumericBounds(name, configName);
    if (!bounds) continue;

    const n = parseNumeric(row.value);
    if (n === null) {
      errors[name] = t("default_param_invalid_number", {
        defaultValue: "Must be a valid number.",
      });
      continue;
    }
    if (bounds.min !== undefined && n < bounds.min) {
      errors[name] = t("default_param_min_error", {
        defaultValue: "Must be at least {{min}}.",
        min: bounds.min,
      });
      continue;
    }
    if (bounds.max !== undefined && n > bounds.max) {
      errors[name] = t("default_param_max_error", {
        defaultValue: "Must be at most {{max}}.",
        max: bounds.max,
      });
    }
  }

  const hasTempBoundsError = T_CHAIN.some((f) => errors[f] !== undefined);
  if (!hasTempBoundsError) {
    const tc = dict.t_cold;
    const tl = dict.t_lukewarm;
    const th = dict.t_hot;
    const tv = dict.t_very_hot;
    const tm = dict.t_max;
    if (tc !== undefined && tl !== undefined && th !== undefined && tv !== undefined && tm !== undefined) {
      if (!(tc < tl && tl < th && th < tv && tv < tm)) {
        const msg = t("default_param_temperature_order_error", {
          defaultValue:
            "Temperatures must be strictly increasing: cold < lukewarm < hot < very hot < max.",
        });
        for (const f of T_CHAIN) {
          errors[f] = msg;
        }
      }
    }
  }

  const pmin = dict.water_heater_min_power;
  const pmax = dict.water_heater_max_power;
  if (
    errors.water_heater_min_power === undefined &&
    errors.water_heater_max_power === undefined &&
    pmin !== undefined &&
    pmax !== undefined
  ) {
    if (!(pmin >= 0 && pmin < pmax)) {
      const msg = t("default_param_water_heater_power_order", {
        defaultValue: "Min power must be ≥ 0 and strictly less than max power.",
      });
      errors.water_heater_min_power = msg;
      errors.water_heater_max_power = msg;
    }
  }

  return errors;
}
